import { RobotoBoardRevisionConflictException } from "@/shared/http";
import { LoggerService } from "@/shared/services/LoggerService";

import { boardRequestFailureReason } from "./boardFailures";
import {
  boardDefinitionTitle,
  updateBoardRequestFor,
  type Board,
  type BoardChange,
  type UpdateBoardRequest,
} from "./BoardRecord";

/**
 * Computes the definition to store from the definition a write is based on.
 *
 * An edit is a function rather than a finished definition because a person can
 * act again before the server answers the previous write. The queue calls each
 * edit only after the write before it has finished, so when that write was
 * saved, the edit receives the definition the server stored for it and builds
 * on it instead of erasing it.
 */
export type BoardDefinitionEdit = (
  base: Record<string, unknown>,
) => Record<string, unknown>;

export type BoardWriteOutcome =
  /**
   * The server stored the write and returned this record, or the edit left
   * the definition as stored and this is the record it was computed from.
   */
  | { status: "saved"; record: Board }
  /**
   * Nothing was stored, because someone else changed the board first: the
   * server refused the write's revision, or the edit threw `BoardEditConflict`.
   * `currentBoard` is the record the server's refusal carried or, for a
   * `BoardEditConflict`, the record the edit was computed from; it stays the
   * same for every write until `adopt`. `proposedDefinition`
   * is the refused edit with every later edit applied to it, in queue order.
   * The person chooses what happens next: reload the board or fork it.
   */
  | {
      status: "conflict";
      currentBoard: Board;
      proposedDefinition: Record<string, unknown>;
    }
  /**
   * The queue was disposed or `isActive` returned false, or `adopt` replaced its
   * record after the write was queued. The queue's record is unchanged.
   */
  | { status: "dropped" }
  /**
   * The edit threw, or the request failed with an error other than a revision
   * conflict. Nothing was stored. `message` says why, in a sentence for the
   * person: the edit's own, or what stopped the request and what to do.
   */
  | { status: "failed"; message: string; error: unknown };

/**
 * Thrown by an edit when the definition it was opened against differs from the
 * definition the queue passes in. The queue sends nothing and returns a
 * `conflict` outcome whose proposed definition is `definition`, and the person
 * then reloads the board or forks it.
 */
export class BoardEditConflict extends Error {
  constructor(readonly definition: Record<string, unknown>) {
    super("The board changed while this edit was open.");
  }
}

/**
 * The writes of one board, as `createBoardWriteQueue` returns them.
 *
 * `save` and `rename` resolve with an outcome for every ending and never reject.
 * Every member is a property holding a function rather than a method, so a
 * caller can pass one on its own: each reads the queue's state through a
 * closure, never through `this`.
 */
export interface BoardWriteQueue {
  /**
   * The acknowledged record: the record the queue was created with, then the
   * record the server returned for each saved write or the record passed to
   * `adopt`, whichever came last. A `displayedRecord` passed to `save` or
   * `rename` is the base of that one write and leaves this record as it was.
   */
  readonly record: Board;
  /** Stop accepting edits and ignore outstanding responses. */
  readonly dispose: () => void;
  /**
   * Replace the definition with the result of `edit`, after every write already
   * queued has finished. An edit that returns the definition it was given, with
   * its members in the same order, sends nothing. `displayedRecord` is the
   * board record the page displays. The queue uses it as the base instead of
   * `record` when it has a higher revision and no conflict is in effect (from a
   * `conflict` outcome until `adopt`).
   */
  readonly save: (
    edit: BoardDefinitionEdit,
    displayedRecord?: Board,
  ) => Promise<BoardWriteOutcome>;
  /**
   * Rename the board, after every write already queued has finished.
   * `displayedRecord` is used as in `save`. When the definition of the record
   * used as the base has a `title` that is a non-empty string, the rename
   * rewrites that title too, so the request carries that record's revision.
   */
  readonly rename: (
    name: string,
    displayedRecord?: Board,
  ) => Promise<BoardWriteOutcome>;
  /**
   * Take `record` as the acknowledged record and end any conflict, discarding
   * the proposed definition. Writes queued before the call resolve as
   * `dropped`; later edits are computed from this record's definition and
   * carry its revision. Call it when the person chooses to reload after a
   * conflict.
   */
  readonly adopt: (record: Board) => void;
}

/** A `conflict` outcome's contents, held by the queue until `adopt`. */
interface BoardWriteConflict {
  currentBoard: Board;
  proposedDefinition: Record<string, unknown>;
}

/**
 * One board's writes, sent one at a time.
 *
 * Definition writes are conditional on the revision they were built from, so two
 * of them in flight at once would make the second fail against a revision the
 * first had just advanced. The queue instead sends one request, waits for the
 * record the server stored, and computes the next edit from it. A person
 * dragging a panel and then renaming a section gets both, in that order, with no
 * conflict of their own making.
 *
 * A board draft's writes go to the draft store in this browser instead of the
 * server. That store answers as the server does: it returns the draft it
 * stored, and it refuses a write built on an older revision with the same
 * exception.
 *
 * After each saved write, `record` is the record the server returned, because
 * the server keeps a definition `title` that is a non-empty string equal to the
 * board name, and so may store a definition that differs from the one sent.
 *
 * After a `conflict` outcome the queue sends nothing until `adopt`: the server
 * has said the queue's record is stale, so every later write built on it would
 * be refused too. Each later edit is applied to the proposed definition and
 * returns another `conflict` outcome; the board page's Fork creates a new board
 * from that definition, and `adopt` discards it.
 *
 * @param initial The record of the board this queue writes, as loaded.
 * @param send Sends one update and resolves with the record the server stored.
 * @param isActive Whether the page that owns the queue is still mounted. A
 *   write that finds it false resolves as `dropped`.
 */
export function createBoardWriteQueue(
  initial: Board,
  send: (request: UpdateBoardRequest) => Promise<Board>,
  isActive: () => boolean,
): BoardWriteQueue {
  let acknowledged = initial;
  let conflict: BoardWriteConflict | null = null;
  let disposed = false;
  let generation = 0;
  /** The write at the back of the queue; the next one waits on it. */
  let queued: Promise<unknown> = Promise.resolve();

  async function attempt(
    describe: (base: Record<string, unknown>) => BoardChange,
    requestedGeneration: number,
    displayedRecord?: Board,
  ): Promise<BoardWriteOutcome> {
    const obsolete = () =>
      disposed || !isActive() || requestedGeneration !== generation;
    if (obsolete()) {
      return { status: "dropped" };
    }
    // While a conflict stands nothing is sent, so the record chosen here goes
    // unused.
    const baseRecord =
      displayedRecord !== undefined &&
      displayedRecord.revision > acknowledged.revision
        ? displayedRecord
        : acknowledged;
    // During a conflict, each edit applies to the proposed definition in queue
    // order, not to the server's definition in `conflict.currentBoard`.
    const base = conflict?.proposedDefinition ?? baseRecord.board_definition;
    let change: BoardChange;
    try {
      change = describe(base);
    } catch (error) {
      if (error instanceof BoardEditConflict) {
        conflict = {
          currentBoard: conflict?.currentBoard ?? baseRecord,
          proposedDefinition: error.definition,
        };
        return { status: "conflict", ...conflict };
      }
      // Any other edit that throws refuses itself in a sentence written for
      // the person.
      return {
        status: "failed",
        message: error instanceof Error ? error.message : String(error),
        error,
      };
    }
    const proposedDefinition = definitionAfter(base, change);
    if (conflict !== null) {
      conflict = { currentBoard: conflict.currentBoard, proposedDefinition };
      return { status: "conflict", ...conflict };
    }
    if (
      "definition" in change &&
      JSON.stringify(change.definition) === JSON.stringify(base)
    ) {
      // The edit left the definition as stored, so there is nothing to
      // send. A request would advance the board's revision, and the server
      // would then refuse an edit another person built on the stored one.
      return { status: "saved", record: baseRecord };
    }

    let stored: Board;
    try {
      stored = await send(updateBoardRequestFor(baseRecord, change));
    } catch (error) {
      if (obsolete()) {
        return { status: "dropped" };
      }
      if (error instanceof RobotoBoardRevisionConflictException) {
        conflict = {
          // A revision refusal always carries the board as it is kept, which
          // the http layer types as `unknown`.
          currentBoard: error.currentBoard as Board,
          proposedDefinition,
        };
        return { status: "conflict", ...conflict };
      }
      // The platform's own words, which can name the board's id or the
      // request's fields, go to the console only.
      LoggerService.warn("A board write failed.", error);
      return {
        status: "failed",
        message: boardRequestFailureReason(error),
        error,
      };
    }

    if (obsolete()) {
      return { status: "dropped" };
    }

    acknowledged = stored;
    return { status: "saved", record: stored };
  }

  function enqueue(
    describe: (base: Record<string, unknown>) => BoardChange,
    displayedRecord?: Board,
  ): Promise<BoardWriteOutcome> {
    // `attempt` resolves for every ending, a failed request included, so one
    // failure never stops the writes queued behind it.
    const requestedGeneration = generation;
    const outcome = queued.then(() =>
      attempt(describe, requestedGeneration, displayedRecord),
    );
    queued = outcome;
    return outcome;
  }

  return {
    get record(): Board {
      return acknowledged;
    },

    dispose: () => {
      disposed = true;
    },

    adopt: (record: Board): void => {
      generation += 1;
      acknowledged = record;
      conflict = null;
    },

    rename: (
      name: string,
      displayedRecord?: Board,
    ): Promise<BoardWriteOutcome> => enqueue(() => ({ name }), displayedRecord),

    save: (
      edit: BoardDefinitionEdit,
      displayedRecord?: Board,
    ): Promise<BoardWriteOutcome> =>
      enqueue((base) => ({ definition: edit(base) }), displayedRecord),
  };
}

/**
 * The definition `base` becomes under `change`, as the server would store it: a
 * rename rewrites a `title` that `boardDefinitionTitle` finds, and leaves any
 * other definition as it is.
 */
function definitionAfter(
  base: Record<string, unknown>,
  change: BoardChange,
): Record<string, unknown> {
  if ("definition" in change) {
    return change.definition;
  }
  if (boardDefinitionTitle(base) !== null) {
    return { ...base, title: change.name };
  }
  return base;
}

/** What to tell a person about a write that was not saved, as an editor's
 *  inline notice and the canvas's error notification word it: that the change
 *  was not saved, then why. Null for a saved write. */
export function boardWriteMessage(
  outcome: Exclude<BoardWriteOutcome, { status: "saved" }>,
): string;
export function boardWriteMessage(outcome: BoardWriteOutcome): string | null;
export function boardWriteMessage(outcome: BoardWriteOutcome): string | null {
  switch (outcome.status) {
    case "saved":
      return null;
    case "conflict":
      return changeNotSavedMessage(
        "This board changed. Reload it, or fork it to keep your unsaved changes.",
      );
    case "dropped":
      return changeNotSavedMessage("This board is no longer open.");
    case "failed":
      return changeNotSavedMessage(outcome.message);
  }
}

/** Says that a change to the board was not saved, then `reason`, the
 *  sentence saying why. */
export function changeNotSavedMessage(reason: string): string {
  return `This change was not saved. ${reason}`;
}
